import * as React from 'react'

const DROPDOWN_WIDTH_PX = 256
const DROPDOWN_GAP_PX = 4

type UseZoomDropdownPositionArgs = {
  open: boolean
  dropdownAlignRef?: React.RefObject<HTMLElement | null>
  containerRef: React.RefObject<HTMLDivElement | null>
}

/**
 * Fixed position for the portaled dropdown: its right edge on the align element's left edge, just
 * below the control. Tracks window scroll/resize (rAF-throttled) while open; null when closed.
 */
export function useZoomDropdownPosition({
  open,
  dropdownAlignRef,
  containerRef,
}: UseZoomDropdownPositionArgs) {
  const [dropdownFixedStyle, setDropdownFixedStyle] = React.useState<React.CSSProperties | null>(
    null
  )
  const dropdownPositionRafRef = React.useRef(0)

  const updateDropdownPosition = React.useCallback(() => {
    if (!open || !dropdownAlignRef?.current || !containerRef.current) {
      return
    }

    const align = dropdownAlignRef.current.getBoundingClientRect()
    const trig = containerRef.current.getBoundingClientRect()
    const left = align.left - DROPDOWN_WIDTH_PX
    const top = trig.bottom + DROPDOWN_GAP_PX

    setDropdownFixedStyle({
      position: 'fixed',
      left,
      top,
      width: DROPDOWN_WIDTH_PX,
      zIndex: 50,
    })
  }, [containerRef, dropdownAlignRef, open])

  const scheduleUpdateDropdownPosition = React.useCallback(() => {
    if (dropdownPositionRafRef.current !== 0) return
    dropdownPositionRafRef.current = requestAnimationFrame(() => {
      dropdownPositionRafRef.current = 0
      updateDropdownPosition()
    })
  }, [updateDropdownPosition])

  React.useEffect(() => {
    if (!open) {
      setDropdownFixedStyle(null)
    }
  }, [open])

  React.useLayoutEffect(() => {
    if (!open || !dropdownAlignRef) {
      return
    }

    updateDropdownPosition()

    window.addEventListener('scroll', scheduleUpdateDropdownPosition, true)
    window.addEventListener('resize', scheduleUpdateDropdownPosition)

    return () => {
      window.removeEventListener('scroll', scheduleUpdateDropdownPosition, true)
      window.removeEventListener('resize', scheduleUpdateDropdownPosition)
      if (dropdownPositionRafRef.current !== 0) {
        cancelAnimationFrame(dropdownPositionRafRef.current)
        dropdownPositionRafRef.current = 0
      }
    }
  }, [dropdownAlignRef, open, updateDropdownPosition, scheduleUpdateDropdownPosition])

  return dropdownFixedStyle
}
